import React from 'react';
import styled, { css } from 'styled-components';

import { perx3PrimaryButton } from 'components/Intelligence/primaryButton';
import { visuallyHidden } from 'components/Intelligence/visuallyHidden';
import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

/**
 * NOTE: this node carries no permission check of its own. The gate is
 * `useCardVisibility().canSendToGrowthPlanner`, and `InsightCardRenderer` passes a `cta` to
 * `InsightCard` only when that flag is true — a second check here would be a second source of
 * truth for the same rule.
 */
export interface IGrowthPlannerCtaProps {
  /** Overrides the shared `Send to Growth Planner` wording; omit it on the four card footers. */
  label?: string;
  onClick: () => void;
  className?: string;
  /**
   * Opt-in, and it defaults to today's behaviour on all ten CTA sites: only Growth Planner Handoff
   * Readiness passes it, when its gates fail. PLA-8815.
   */
  disabled?: boolean;
  /** Announced on focus when `disabled`; the visible reason lives in the note beside the button. */
  disabledReason?: string;
}

/** The drawn primary-button fill. See the note on `background` below for why it is not a token. */
const CTA_FILL = tokens.color['accent-indigo'];

/**
 * Radius and padding are the file's, read off every CTA the screens draw: `589:28061` and
 * `589:30480` on frame A and `589:33195` and `589:32895` on frame B all carry an 8px radius, and
 * `589:32895` — the only one authored as a hug rather than a fixed box — pads 8 / 11. 11 is off the
 * space scale and ships at the nearest step. The four disagree about the FILL and nothing else, so
 * that one stays on the shared accent pending a designer's ruling (see the note below).
 *
 * There is no elevation at rest: none of those four nodes carries an effects layer, on a Properties
 * read proved against three nodes that do (`589:28683`, `615:2672`, `621:3810` all print
 * `Shadows and blurs`). The focus ring is therefore the button's only shadow and has nothing to
 * composite over — which is why it is named alone below rather than stacked on `elevation.cta`.
 */
const CtaButton = styled.button<{ $disabled?: boolean; $perx3: boolean }>`
  padding: ${tokens.space['8']} ${tokens.space['12']};
  border: none;
  border-radius: ${tokens.radius.sm};
  /* PLA-8828 settles what PLA-8815 had parked. Layer 1's two frames painted the same node two
     different colours -- 589:30480 #4F46E5 against its 589:33195 twin #3D8BFF -- so the fill sat on
     the shared accent pending a designer. Customer 360's Frame A resolves it: all eight BUTTON
     nodes read #4F46E5 with nothing disagreeing (3583-10091, 3583-9739, 3583-10765, 3583-9123,
     3583-8145, 3583-8578, 3583-11184, 3583-7625; figma-spec.md 208 names it the primary-button
     token). Its own --is-accent-indigo rather than --is-accent-purple: that token is #6D5DFC and
     drives the tab and gradient roles too, so moving it would repaint them off a CTA reading. */
  background: ${CTA_FILL};
  color: ${tokens.color['text-primary']};
  cursor: pointer;
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
  white-space: nowrap;

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }

  /* PLA-8815. UNVERIFIED against Figma: the file's only grey CTA node is unresolved on fill, on
     label and on which card owns it, and two of our own reads of it disagree — so this is derived
     from tokens rather than lifted from that node. --is-track-bg is the palette's inert filled
     surface and --is-text-muted its lowest-emphasis ink, which is the reduced-emphasis pair the
     theme already carries; measured 3.9:1, so the label stays legible while the control reads as
     unavailable. The ENABLED path above is untouched -- its fill is still with the designer. */
  /* Round 4 (user): the redesign's primary button is black, and it is layered here rather than
     replacing the declarations above so the flag-OFF CTA still draws the #4F46E5 the frames do. */
  ${({ $perx3 }) => $perx3 && perx3PrimaryButton}

  ${({ $disabled }) =>
    $disabled &&
    css`
      background: ${tokens.color['track-bg']};
      color: ${tokens.color['text-muted']};
      cursor: not-allowed;
    `}
`;

const HiddenReason = styled.span`
  ${visuallyHidden}
`;

const GrowthPlannerCta: React.FC<IGrowthPlannerCtaProps> = ({
  label,
  onClick,
  className,
  disabled,
  disabledReason
}) => {
  const { t } = useMultiLanguageTranslation();
  const isPerx3 = usePerx3Redesign();
  const reasonId = disabled && disabledReason ? 'growth-planner-cta-reason' : undefined;

  /* `aria-disabled` rather than the native `disabled` attribute, chosen on what each does to focus
     order: `disabled` drops the button OUT of the tab order, so a keyboard or screen-reader user
     never reaches it and never hears why it is unavailable — which is the one thing this state has
     to communicate. `aria-disabled` keeps it focusable and announces it as unavailable, and the
     handler below is what actually makes it inert. Both together would be contradictory. */
  return (
    <>
      <CtaButton
        type="button"
        className={className}
        onClick={disabled ? undefined : onClick}
        aria-disabled={disabled || undefined}
        aria-describedby={reasonId}
        $disabled={disabled}
        $perx3={isPerx3}
        data-testid="growth-planner-cta"
      >
        {label || t(`${TCustomerInsights}.Handoff.CtaLabel`)}
      </CtaButton>
      {reasonId ? <HiddenReason id={reasonId}>{disabledReason}</HiddenReason> : null}
    </>
  );
};

export default GrowthPlannerCta;
